import zod from 'zod';
import { HomeAssistantIntegrationFormSchema } from '../../../services/IntegrationService/HomeAssistantService/schemas';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useContext, useEffect } from 'react';
import { AppContext } from '../../../context/AppContextProvider';
import {
  useCreateHomeAssistantIntegrationAsync,
  useGetHomeAssistantIntegrationByIdAsync,
  useUpdateHomeAssistantIntegrationAsync,
} from '../../../services/IntegrationService/HomeAssistantService/hooks/useMutation';
import { Box, Grid2 } from '@mui/material';
import TextFieldComponent from '../../../components/TextFieldComponent';
import TooltipComponent from '../../../components/TooltipComponent';
import SwitchComponent from '../../../components/SwitchComponent';
import ButtonComponent from '../../../components/ButtonComponent';
import { t } from 'i18next';

interface IHomeAssistantEditionFormProps {
  homeAssistantIdToUpdate?: number;
  handleSaveForm?: () => void;
}

type HomeAssistantEditionFormInput = zod.infer<
  typeof HomeAssistantIntegrationFormSchema
>;

const HomeAssistantEditionForm = ({
  homeAssistantIdToUpdate,
  handleSaveForm,
}: IHomeAssistantEditionFormProps) => {
  const { control, reset, handleSubmit } =
    useForm<HomeAssistantEditionFormInput>({
      resolver: zodResolver(HomeAssistantIntegrationFormSchema),
      defaultValues: {
        name: '',
        description: '',
        isActive: true,
        homeAssistantBaseUrl: '',
        homeAssistantAccessToken: '',
      },
    });
  const { mutateAsync: mutateGet } = useGetHomeAssistantIntegrationByIdAsync();
  const { mutateAsync: mutateCreate } =
    useCreateHomeAssistantIntegrationAsync();
  const { mutateAsync: mutateUpdate } =
    useUpdateHomeAssistantIntegrationAsync();
  const { showSnackbar } = useContext(AppContext);

  const handleSave = async (data: HomeAssistantEditionFormInput) => {
    if (homeAssistantIdToUpdate) {
      await mutateUpdate({ id: homeAssistantIdToUpdate, data }).then(() => {
        saveIntegration('Integração Home Assistant atualizada com sucesso.');
      });
    } else {
      await mutateCreate(data).then(() => {
        saveIntegration('Integração Home Assistant criada com sucesso.');
      });
    }
  };

  const saveIntegration = (message: string) => {
    reset();
    showSnackbar(message, 'success');
    if (handleSaveForm) handleSaveForm();
  };

  useEffect(() => {
    if (homeAssistantIdToUpdate) {
      mutateGet(homeAssistantIdToUpdate).then((i) =>
        reset(i, { keepDefaultValues: true }),
      );
    }
  }, [homeAssistantIdToUpdate]);

  return (
    <>
      <Grid2
        container
        direction={'column'}
        justifyContent={'center'}
        alignItems={'center'}
        size={12}
        spacing={3}
      >
        <Grid2
          display={'flex'}
          flexDirection={{ xs: 'column', md: 'row' }}
          justifyContent={'center'}
          alignItems={{ xs: 'center', md: 'normal' }}
          size={12}
          gap={{ xs: 0, md: 3 }}
        >
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('integrationsPage.homeAssistantForms.name')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'name'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('integrationsPage.homeAssistantForms.description')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  multiline
                  maxRows={4}
                />
              )}
              name={'description'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <Box width={'14%'}>
                  <TooltipComponent
                    title={value ? 'Ativo' : 'Inativo'}
                    placement={'right'}
                  >
                    <SwitchComponent
                      label={t('integrationsPage.homeAssistantForms.status')}
                      value={value}
                      onChange={(value) => onChange(value)}
                      color={'primary'}
                    />
                  </TooltipComponent>
                </Box>
              )}
              name={'isActive'}
              control={control}
            />
          </Grid2>
          <Grid2
            size={{ xs: 12, lg: 6 }}
            display={'column'}
            alignItems={'center'}
            justifyContent={'center'}
          >
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t('integrationsPage.homeAssistantForms.baseUrl')}
                  onChangeText={(val) => onChange(val)}
                  type={'text'}
                  errorMessage={error?.message}
                  value={value}
                  placeholder={'http://192.168.0.10:8123'}
                />
              )}
              name={'homeAssistantBaseUrl'}
              control={control}
            />
            <Controller
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <TextFieldComponent
                  label={t(
                    'integrationsPage.homeAssistantForms.accessToken',
                  )}
                  onChangeText={(val) => onChange(val)}
                  type={'password'}
                  errorMessage={error?.message}
                  value={value}
                />
              )}
              name={'homeAssistantAccessToken'}
              control={control}
            />
          </Grid2>
        </Grid2>
        <ButtonComponent
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleSubmit(handleSave)}
        >
          {t('integrationsPage.homeAssistantForms.save')}
        </ButtonComponent>
      </Grid2>
    </>
  );
};

export default HomeAssistantEditionForm;
